// utils/productStatusHelpers.tsx
import { Clock, AlertCircle, CheckCircle, XCircle, FileText, Archive } from 'lucide-react';

interface StatusIndicatorProps {
    status: string;
    showTooltip?: boolean;
    isStaff?: boolean;
}

export const getStatusIndicator = ({ status, showTooltip = true, isStaff = false }: StatusIndicatorProps) => {
    const statusConfig = {
        draft: {
            icon: FileText,
            label: 'Draft',
            bgColor: 'bg-gray-100',
            borderColor: 'border-gray-300',
            textColor: 'text-gray-700',
            iconColor: 'text-gray-500',
            tooltip: 'This product is a draft and not yet submitted'
        },
        pending_review: {
            icon: Clock,
            label: isStaff ? 'Under Review' : 'Pending Review',
            bgColor: 'bg-yellow-100',
            borderColor: 'border-yellow-200',
            textColor: 'text-yellow-800',
            iconColor: 'text-yellow-600',
            tooltip: isStaff ? 'Reviewing this product before making a decision' : 'This product is pending review'
        },
        changes_requested: {
            icon: AlertCircle,
            label: 'Changes Required',
            bgColor: 'bg-orange-100',
            borderColor: 'border-red-200',
            textColor: 'text-orange-800',
            iconColor: 'text-orange-600',
            tooltip: 'Changes have been requested. Please make updates and resubmit.'
        },
        approved: {
            icon: CheckCircle,
            label: 'Approved',
            bgColor: 'bg-green-100',
            borderColor: 'border-green-200',
            textColor: 'text-green-800',
            iconColor: 'text-green-600',
            tooltip: 'This product is approved and live'
        },
        rejected: {
            icon: XCircle,
            label: 'Rejected',
            bgColor: 'bg-red-100',
            borderColor: 'border-red-300',
            textColor: 'text-red-800',
            iconColor: 'text-red-700',
            tooltip: 'This product has been rejected'
        },
        archived: {
            icon: Archive,
            label: 'Archived',
            bgColor: 'bg-gray-100',
            borderColor: 'border-gray-300',
            textColor: 'text-gray-600',
            iconColor: 'text-gray-500',
            tooltip: 'This product is archived'
        },
        deleted: {
            icon: XCircle,
            label: 'Deleted',
            bgColor: 'bg-gray-200',
            borderColor: 'border-gray-400',
            textColor: 'text-gray-600',
            iconColor: 'text-gray-500',
            tooltip: 'This product has been deleted'
        }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    const Icon = config.icon;

    return (
        <div 
            className={`flex items-center gap-1.5 px-2.5 py-1 border rounded-full shrink-0 ${config.bgColor} ${config.borderColor}`}
            title={showTooltip ? config.tooltip : undefined}
        >
            <Icon className={`h-3.5 w-3.5 ${config.iconColor}`} />
            <span className={`text-xs font-medium ${config.textColor}`}>
                {config.label}
            </span>
        </div>
    );
};